import { timeAgo } from "@/lib/api";

const LiveChip = ({ updatedAt }: { updatedAt?: string }) => {
  if (!updatedAt) return null;
  const fresh = Date.now() - new Date(updatedAt).getTime() < 15 * 60 * 1000;
  return fresh ? (
    <span data-testid="policy-pulse-live" className="inline-flex items-center gap-1.5 rounded-full border border-[#A78BFA]/40 bg-[#A78BFA]/10 px-2 py-0.5 font-mono text-[9px] font-bold tracking-[0.18em] text-[#A78BFA]">
      <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-[#A78BFA]" />
      LIVE
    </span>
  ) : (
    <span data-testid="policy-pulse-updated" className="font-mono text-[9px] uppercase tracking-widest text-gray-500">
      Updated {timeAgo(updatedAt)}
    </span>
  );
};

const Cell = ({ label, value, testid, title }: {
  label: string; value: number | null | undefined; testid: string; title?: string;
}) => (
  <div className="px-4 py-3 sm:px-5 min-w-0" data-testid={testid} title={title}>
    <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-gray-500 leading-snug xl:min-h-[25px]">{label}</p>
    <div className="flex items-baseline gap-2 mt-1">
      <span className="font-mono text-lg sm:text-xl font-bold text-white">
        {value != null ? String(value) : "—"}
      </span>
    </div>
  </div>
);

export const PolicyHero = ({ data }: { data: any }) => {
  const pulse = data?.pulse || {};
  const defs = pulse.definitions || {};
  return (
    <section className="relative overflow-hidden border-b border-white/[0.06]" data-testid="policy-sector-hero">
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <img src="/hero/policy-sector.jpg" alt="" fetchPriority="high"
          className="w-full h-full object-cover object-[62%_45%]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#000000] via-[#000000]/60 to-[#000000]/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#000000] via-transparent to-[#000000]/45" />
      </div>
      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-10 lg:py-12">
        <div className="max-w-3xl flex flex-col">
            <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#A78BFA]">Sector</p>
            <h1 data-testid="policy-masthead"
              className="font-heading font-black text-white text-5xl sm:text-6xl xl:text-7xl tracking-tight mt-1.5">
              POLICY
            </h1>
            <p className="text-gray-300 text-[15px] sm:text-base mt-3 max-w-xl leading-relaxed">
              {data?.sector?.description || "Regulation, jurisdictions, enforcement and the policy forces shaping the onchain economy."}
            </p>

            <div className="mt-6">
              <div className="flex items-center gap-3">
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-[#00E5FF]">Policy Pulse</p>
                <LiveChip updatedAt={pulse.updated_at} />
              </div>
              <div data-testid="policy-pulse"
                className="mt-3 inline-grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 rounded-xl border border-white/[0.09] bg-black/50 backdrop-blur-md divide-x divide-y xl:divide-y-0 divide-white/[0.06] overflow-hidden">
                <Cell testid="pulse-tracked-regulations" label="Tracked Regulations"
                  value={pulse.tracked_regulations} title={defs.tracked_regulations} />
                <Cell testid="pulse-tracked-jurisdictions" label="Tracked Jurisdictions"
                  value={pulse.tracked_jurisdictions} title={defs.tracked_jurisdictions} />
                <Cell testid="pulse-policy-articles" label="Policy Stories · 24H"
                  value={pulse.articles_24h} title={defs.articles_24h} />
                <Cell testid="pulse-enforcement-7d" label="Enforcement Stories · 7D"
                  value={pulse.enforcement_stories_7d} title={defs.enforcement_stories_7d} />
                <Cell testid="pulse-linked-coverage" label="Linked Coverage"
                  value={pulse.linked_coverage} title={defs.linked_coverage} />
              </div>
            </div>

            <p className="pt-8 font-mono text-[9px] uppercase tracking-[0.18em] text-gray-600"
              data-testid="policy-provenance">
              Regulation + Jurisdiction + Impact Intelligence · NewsLayer Regulation Tracker · Link graph auto-refreshed
            </p>
        </div>
        {/* Restrained atmosphere on the artwork side — sector doctrine, not a module */}
        <div aria-hidden
          className="hidden lg:flex absolute right-10 top-1/2 -translate-y-1/2 flex-col items-end gap-2.5 font-mono text-[10px] tracking-[0.42em] uppercase select-none pointer-events-none">
          <span className="text-white/[0.30]">Regulation</span>
          <span className="text-white/[0.20]">Jurisdiction</span>
          <span className="text-white/[0.30]">Impact</span>
          <span className="h-10 w-[1px] mr-0.5 bg-gradient-to-b from-[#A78BFA]/50 to-transparent" />
        </div>
      </div>
    </section>
  );
};
